/**
 * The manual — OpenComply's built-in knowledge base.
 *
 * Two views from one file, chosen by the route:
 *   /manual          the index: the ordered getting-started path, a search box,
 *                    and every article grouped by section.
 *   /manual/:slug    one article: what the screen is for, the steps to work
 *                    through, the easy-to-miss things, and where to go next.
 *
 * The content lives in lib/manual.ts (in code, English — the owner's decision),
 * so this file is only the reader. The on-screen "?" (components/Help.tsx)
 * deep-links here via manualSlugForRoute, and the article's "related" links go
 * either to another article (a bare slug) or straight into the app (a route).
 *
 * No API, no data fetching: the manual ships with the build.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MANUAL_ARTICLES,
  MANUAL_GROUPS,
  GETTING_STARTED_PATH,
  articleBySlug,
  articlesInGroup,
  isRouteLink,
  type ManualArticle,
  type ManualLink,
} from '../lib/manual';
import { Card, EmptyState } from '../components/ui';
import { IconBook, IconChevron } from '../components/icons';

// ---------------------------------------------------------------------------
//  Entry point — index or single article
// ---------------------------------------------------------------------------

export function ManualPage() {
  const { slug } = useParams();
  if (slug) return <ManualArticleView slug={slug} />;
  return <ManualIndex />;
}

// ---------------------------------------------------------------------------
//  The index
// ---------------------------------------------------------------------------

function ManualIndex() {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!q) return null;
    return MANUAL_ARTICLES.filter((a) =>
      [a.title, a.summary, a.purpose, ...(a.tips ?? [])]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [q]);

  return (
    <div className="oc-page space-y-6 animate-fade-in">
      <header className="flex items-start gap-3">
        <span className="mt-1 shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-brand/10 text-brand">
          <IconBook className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <h1 className="oc-h1">Manual</h1>
          <p className="oc-muted mt-1 max-w-prose">
            How OpenComply works, screen by screen — and the order to do things in. Reach for the
            "?" on any screen to jump straight to its page here.
          </p>
        </div>
      </header>

      <div className="max-w-prose">
        <label className="sr-only" htmlFor="manual-search">Search the manual</label>
        <input
          id="manual-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the manual…"
          className="oc-input w-full"
          autoComplete="off"
        />
      </div>

      {matches ? (
        <section aria-label="Search results" className="space-y-3">
          <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">
            {matches.length === 0
              ? 'No matching articles'
              : `${matches.length} ${matches.length === 1 ? 'article' : 'articles'}`}
          </p>
          {matches.length === 0 ? (
            <EmptyState title="Nothing found" description="Try a different word, or browse the sections below." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {matches.map((a) => <ArticleCard key={a.slug} article={a} />)}
            </div>
          )}
        </section>
      ) : (
        <>
          <GettingStartedCard />
          {MANUAL_GROUPS.map((group) => {
            const articles = articlesInGroup(group.id);
            if (articles.length === 0) return null;
            return (
              <section key={group.id} aria-label={group.title} className="space-y-3">
                <div>
                  <h2 className="oc-h2">{group.title}</h2>
                  <p className="oc-muted">{group.blurb}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {articles.map((a) => <ArticleCard key={a.slug} article={a} />)}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

function GettingStartedCard() {
  return (
    <Card className="bg-brand/[0.03] border-brand/20">
      <div className="mb-3">
        <h2 className="oc-h2">Getting started, in order</h2>
        <p className="oc-muted">New here? Work down this path — each step links to its full article.</p>
      </div>
      <ol className="space-y-1.5">
        {GETTING_STARTED_PATH.map((step) => (
          <li key={step.n}>
            <Link
              to={`/manual/${step.slug}`}
              className="flex items-start gap-3 rounded-lg px-2.5 py-2 -mx-2.5 hover:bg-surface-sunken transition-colors group"
            >
              <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full bg-brand/10 text-brand text-xs font-semibold tabular-nums mt-0.5">
                {step.n}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-ink">{step.title}</span>
                <span className="block oc-muted">{step.blurb}</span>
              </span>
              <IconChevron className="w-4 h-4 text-ink-muted shrink-0 mt-1" aria-hidden />
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function ArticleCard({ article }: { article: ManualArticle }) {
  return (
    <Link to={`/manual/${article.slug}`} className="group block">
      <Card className="h-full transition-colors group-hover:border-brand/40">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-ink text-balance">{article.title}</h3>
          <IconChevron className="w-4 h-4 text-ink-muted shrink-0 mt-1 group-hover:text-brand" aria-hidden />
        </div>
        <p className="oc-muted mt-1">{article.summary}</p>
      </Card>
    </Link>
  );
}

// ---------------------------------------------------------------------------
//  One article
// ---------------------------------------------------------------------------

function ManualArticleView({ slug }: { slug: string }) {
  const article = articleBySlug(slug);

  if (!article) {
    return (
      <div className="oc-page space-y-5 animate-fade-in">
        <BackToManual />
        <EmptyState
          title="No such article"
          description="That page of the manual does not exist. Head back to the contents."
        />
      </div>
    );
  }

  const group = MANUAL_GROUPS.find((g) => g.id === article.group);

  return (
    <div className="oc-page max-w-3xl space-y-6 animate-fade-in">
      <BackToManual />

      <header className="space-y-2">
        {group && (
          <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">
            {group.title}
          </p>
        )}
        <h1 className="oc-h1 text-balance">{article.title}</h1>
        <p className="text-base text-ink-secondary max-w-prose">{article.summary}</p>
      </header>

      <Card>
        <p className="text-ink leading-relaxed max-w-prose">{article.purpose}</p>
      </Card>

      {article.steps && article.steps.length > 0 && (
        <section className="space-y-3">
          <h2 className="oc-h2">How to work through it</h2>
          <ol className="space-y-3">
            {article.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="shrink-0 grid place-items-center w-7 h-7 rounded-full bg-brand/10 text-brand text-sm font-semibold tabular-nums mt-0.5">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-ink">{step.title}</p>
                  <p className="oc-muted mt-0.5 max-w-prose">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {article.tips && article.tips.length > 0 && (
        <section className="space-y-2">
          <h2 className="oc-h2">Good to know</h2>
          <ul className="space-y-1.5">
            {article.tips.map((tip, i) => (
              <li key={i} className="flex gap-2 text-sm text-ink-secondary max-w-prose">
                <span className="mt-2 w-1 h-1 rounded-full bg-ink-muted shrink-0" aria-hidden />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {article.related && article.related.length > 0 && (
        <section className="space-y-2">
          <h2 className="oc-h2">Where to go next</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {article.related.map((link) => <RelatedLink key={link.to} link={link} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function RelatedLink({ link }: { link: ManualLink }) {
  const to = isRouteLink(link.to) ? link.to : `/manual/${link.to}`;
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
    >
      {link.label}
      <IconChevron className="w-4 h-4" aria-hidden />
    </Link>
  );
}

function BackToManual() {
  return (
    <Link
      to="/manual"
      className="inline-flex items-center gap-1 text-sm text-ink-secondary hover:text-ink"
    >
      <IconChevron className="w-4 h-4 rotate-180" aria-hidden />
      All articles
    </Link>
  );
}
